import { z } from 'zod';
import { MobIdSchema } from './mobs.js';
import { PropPlacementSchema, PROPS } from './props.js';
import RAW_WORLD_EDITS from './world-edits.json' with { type: 'json' };
/**
 * Hand retouches of the generated world (ADR 0022), made in the in-game
 * editor by an admin and saved to `world-edits.json`. The world stays
 * generated; these edits are applied on top of it, in the same way on the
 * server, the client and the previews.
 *
 * A generated element has no id, so an edit names it by what it is and where
 * the generator put it (`prop` + `x`, `y`; `mob` + `x`, `y`). A portal and a
 * chest have ids of their own. When a change to the generator moves the
 * element, the edit no longer finds it and is dropped (reported as stale).
 */
const TileSchema = z.object({ x: z.number().int().min(0), y: z.number().int().min(0) });
/**
 * Props tied to something else than their tile, which the editor leaves be:
 * stairs and bridges to the terrain they cross, the cave's ways in and out
 * to their exits, the portal and the chests to their own edits.
 */
export const FIXED_PROP_IDS = new Set([
    'bridge-h',
    'bridge-v',
    'stairs-grass',
    'stairs-sand',
    'stairs-snow',
    'stairs-marsh',
    'cave-entrance',
    'summit-hole',
    'cave-ladder',
    'cave-daylight',
    'cave-exit',
    'portal',
    'chest',
    'chest-locked',
    'chest-open',
]);
export function isEditableProp(prop) {
    return prop in PROPS && !FIXED_PROP_IDS.has(prop);
}
const EditablePropSchema = z
    .string()
    .refine(isEditableProp, { message: 'This prop cannot be edited' });
export const PropEditSchema = z.object({
    prop: EditablePropSchema,
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** Where it stands now; null when it was removed. */
    to: TileSchema.extend({ flip: z.boolean().default(false) }).nullable(),
});
const FacingSchema = z.enum(['north', 'east', 'south', 'west']);
/** How many it keeps alive, and how far they wander: the same bounds as a zone's spawns. */
const SpawnCountSchema = z.number().int().min(1).max(20);
const SpawnRadiusSchema = z.number().int().min(0).max(8);
export const SpawnEditSchema = z.object({
    mob: MobIdSchema,
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** Where it stands now, which way, how many and how far; null when it was removed. */
    to: TileSchema.extend({
        facing: FacingSchema.optional(),
        count: SpawnCountSchema.optional(),
        radius: SpawnRadiusSchema.optional(),
    }).nullable(),
});
/** An inhabitant or a den the admin set down: townsfolk, a merchant, a pack of wolves. */
export const AddedSpawnSchema = TileSchema.extend({
    mob: MobIdSchema,
    count: SpawnCountSchema.default(1),
    radius: SpawnRadiusSchema.default(0),
    facing: FacingSchema.optional(),
});
/** A portal moves whole: its arch, and the tile travellers land on with it. */
export const PortalEditSchema = TileSchema.extend({ id: z.string().min(1) });
export const ChestEditSchema = TileSchema.extend({ id: z.string().min(1) });
export const WorldEditsSchema = z.object({
    props: z.array(PropEditSchema).default([]),
    added: z
        .array(PropPlacementSchema.refine((p) => isEditableProp(p.prop), { message: 'Fixed prop' }))
        .default([]),
    spawns: z.array(SpawnEditSchema).default([]),
    addedSpawns: z.array(AddedSpawnSchema).default([]),
    portals: z.array(PortalEditSchema).default([]),
    chests: z.array(ChestEditSchema).default([]),
});
export const NO_WORLD_EDITS = WorldEditsSchema.parse({});
/** The edits saved in the repository, applied to the world at load. */
export const WORLD_EDITS = WorldEditsSchema.parse(RAW_WORLD_EDITS);
//# sourceMappingURL=world-edits.js.map